import axios from "../../../../shared/axios/axiosProxy.js";
import {
  applyIndustryFinancialDefaultsToStore,
  persistIndustryWithFinancialDefaults,
} from "../persistIndustryWithFinancialDefaults.js";
import { mapCegjelzoCompanyToIndustry } from "../teaor/mapCegjelzoCompanyToIndustry.js";
import {
  mapCegjelzoCompanyToCompanyType,
  mapCegjelzoCompanyToNoOfEmployees,
  mapCegjelzoCompanyToVatFilter,
  mapCegjelzoCompanyToYearlyRevenue,
} from "./mapCegjelzoCompanyData.js";

const isValidSearchID = (searchID) =>
  searchID && searchID !== "undefined" && searchID !== "null";

/**
 * Apply Cégjelző-derived `companytype` to Redux (and optional flow UI state).
 * Overwrites a prior manual-path selection when the user switches to auto-fill.
 */
export const applyCegjelzoCompanyTypeToStore = ({
  companyData,
  setCompanyTypeFlowSelection,
  addCompanyInfo,
}) => {
  const companytype = mapCegjelzoCompanyToCompanyType(companyData);
  if (!companytype) {
    return undefined;
  }

  if (typeof setCompanyTypeFlowSelection === "function") {
    setCompanyTypeFlowSelection(companytype);
  } else if (typeof addCompanyInfo === "function") {
    addCompanyInfo({
      id: "companytype",
      data: companytype,
      stateObject: "company_store",
    });
  }

  return companytype;
};

/**
 * Apply Cégjelző `yearlyrevenue` to Redux when revenue is present (COMPANIES only).
 * When `fallBackToIndustryDefaults` is true and Cégjelző has no revenue, writes
 * industry + financial defaults (yearlyrevenue, incoming/outgoing invoices) so stale
 * COMPANIES revenue does not linger — same smart-fake as on persist.
 */
export const applyCegjelzoYearlyRevenueToStore = ({
  companyData,
  addCompanyInfo,
  fallBackToIndustryDefaults = false,
}) => {
  const yearlyrevenue = mapCegjelzoCompanyToYearlyRevenue(companyData);
  if (yearlyrevenue !== undefined) {
    if (typeof addCompanyInfo === "function") {
      addCompanyInfo({
        id: "yearlyrevenue",
        data: yearlyrevenue,
        stateObject: "company_store",
      });
      addCompanyInfo({
        id: "yearlyrevenueSource",
        data: "default",
        stateObject: "company_store",
      });
    }
    return yearlyrevenue;
  }

  if (!fallBackToIndustryDefaults || !companyData) {
    return undefined;
  }

  const industry = mapCegjelzoCompanyToIndustry(companyData) ?? "other";
  if (typeof addCompanyInfo === "function") {
    addCompanyInfo({
      id: "industry",
      data: industry,
      stateObject: "company_store",
    });
    addCompanyInfo({
      id: "industrySource",
      data: "default",
      stateObject: "company_store",
    });
    if (industry !== "other") {
      addCompanyInfo({
        id: "industryinput",
        data: "",
        stateObject: "company_store",
      });
    }
  }

  const financialDefaults = applyIndustryFinancialDefaultsToStore({
    industry,
    addCompanyInfo,
  });

  return financialDefaults?.yearlyrevenue;
};

/**
 * Apply Cégjelző adószám 9th digit → `vatFilter` in Redux.
 * Clears to `""` when the new company has no parseable tax number so a prior
 * company's VAT state does not linger (same pattern as `noofemployees` reset).
 */
export const applyCegjelzoVatFilterToStore = ({
  companyData,
  addCompanyInfo,
}) => {
  const mapped = mapCegjelzoCompanyToVatFilter(companyData);
  const vatFilter = mapped ?? "";

  if (typeof addCompanyInfo === "function") {
    addCompanyInfo({
      id: "vatFilter",
      data: vatFilter,
      stateObject: "company_store",
    });
  }

  return vatFilter;
};

/**
 * Persist selected Cégjelző company data and apply derived industry + companytype mapping.
 * Falls back to `other` when TEÁOR mapping does not resolve (auto-fill flow only).
 * @returns {Promise<{ industry: string, companytype?: string } | null>}
 */
export const persistCegjelzoCompany = async ({
  searchID,
  companyData,
  saveCegjelzoData,
  addCompanyInfo,
  setCompanyTypeFlowSelection,
}) => {
  await axios.post("/api/cegjelzo/persist", {
    searchID,
    companyData,
  });

  if (typeof saveCegjelzoData === "function") {
    saveCegjelzoData({ ...companyData });
  }

  const mappedIndustry = mapCegjelzoCompanyToIndustry(companyData);
  const industry = mappedIndustry ?? "other";
  const companytype = applyCegjelzoCompanyTypeToStore({
    companyData,
    setCompanyTypeFlowSelection,
    addCompanyInfo,
  });

  const noofemployees = mapCegjelzoCompanyToNoOfEmployees(companyData);

  if (companytype) {
    if (isValidSearchID(searchID)) {
      const companytypePatch = {
        companytype,
        noofemployees: noofemployees ?? 0,
      };
      await axios.patch(`/api/search/${searchID}`, companytypePatch, {
        headers: { "Content-Type": "application/json" },
      });
    }
  } else if (noofemployees !== undefined && isValidSearchID(searchID)) {
    await axios.patch(
      `/api/search/${searchID}`,
      { noofemployees },
      { headers: { "Content-Type": "application/json" } },
    );
  }

  if (typeof addCompanyInfo === "function") {
    await persistIndustryWithFinancialDefaults({
      industry,
      searchID,
      addCompanyInfo,
      clearIndustryInputWhenNotOther: true,
      // Cégjelző TEÁOR mapping, not a human pick — flag it even when it resolves
      // to a real industry, since the person never confirmed it themselves.
      industrySource: "default",
    });
  }

  // Map and persist yearlyrevenue after industry defaults so Cégjelző revenue overrides.
  // API patch runs regardless of addCompanyInfo (same as companytype / noofemployees above).
  const yearlyrevenue = applyCegjelzoYearlyRevenueToStore({
    companyData,
    addCompanyInfo,
  });

  if (yearlyrevenue !== undefined && isValidSearchID(searchID)) {
    await axios.patch(
      `/api/search/${searchID}`,
      { yearlyrevenue },
      { headers: { "Content-Type": "application/json" } },
    );
  }

  const vatFilter = applyCegjelzoVatFilterToStore({
    companyData,
    addCompanyInfo,
  });

  if (isValidSearchID(searchID)) {
    await axios.patch(
      `/api/search/${searchID}`,
      { vatFilter },
      { headers: { "Content-Type": "application/json" } },
    );
  }

  return { industry, companytype, yearlyrevenue, vatFilter };
};
